import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

/**
 * Stored API key catalog. Keys live in `expo-secure-store` on native
 * (iOS Keychain / Android Keystore) and `localStorage` on web (still better
 * than committing them; not a security boundary for a single-user
 * local-first app).
 *
 * Never log key values. Never include them in PDF binders, exports, or
 * support payloads — they belong only at the call site that uses them.
 */

export interface ApiKeySlot {
  key: string;
  label: string;
  description: string;
  placeholder: string;
  signupUrl: string;
}

export const API_KEY_SLOTS: ApiKeySlot[] = [
  {
    key: 'google_vision',
    label: 'Google Vision API',
    description:
      'Powers receipt OCR auto-fill in inventory. Restrict the key to Vision + Geocoding + Places in Cloud Console.',
    placeholder: 'AIzaSy…',
    signupUrl:
      'https://console.cloud.google.com/apis/library/vision.googleapis.com',
  },
  {
    key: 'google_places',
    label: 'Google Maps key (Places + Static Maps)',
    description:
      'Powers address autocomplete in Edit my home AND the map preview on My Home. Enable Places API (New) + Maps Static API on the same key in Cloud Console.',
    placeholder: 'AIzaSy…',
    signupUrl: 'https://console.cloud.google.com/apis/library/places.googleapis.com',
  },
  {
    key: 'openweather',
    label: 'OpenWeatherMap (fallback)',
    description:
      'Only needed for non-US ZIPs — NOAA covers US for free. 1000 calls/day on the free tier.',
    placeholder: '32-char hex…',
    signupUrl: 'https://openweathermap.org/api',
  },
];

const PREFIX = 'homestead.apikey.';

export async function setApiKey(slot: string, value: string): Promise<void> {
  const trimmed = value.trim();
  if (Platform.OS === 'web') {
    if (trimmed) {
      // eslint-disable-next-line no-undef
      window.localStorage.setItem(PREFIX + slot, trimmed);
    } else {
      // eslint-disable-next-line no-undef
      window.localStorage.removeItem(PREFIX + slot);
    }
    return;
  }
  if (trimmed) {
    await SecureStore.setItemAsync(PREFIX + slot, trimmed);
  } else {
    await SecureStore.deleteItemAsync(PREFIX + slot);
  }
}

export async function getApiKey(slot: string): Promise<string | null> {
  if (Platform.OS === 'web') {
    // eslint-disable-next-line no-undef
    return window.localStorage.getItem(PREFIX + slot);
  }
  return await SecureStore.getItemAsync(PREFIX + slot);
}

/** True if the slot has any value. We never expose the value itself for display. */
export async function hasApiKey(slot: string): Promise<boolean> {
  const v = await getApiKey(slot);
  return v != null && v.length > 0;
}

/** Mask for display: AIzaSy*****wxYZ (last 4 chars). Used for "Currently set" hints. */
export function maskKey(value: string | null): string {
  if (!value || value.length < 8) return '';
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}
